import { ExportIcon, PrintIcon } from "@xola/icons";
import { Button, flash, Modal } from "@xola/ui-kit";
import { PurchaseItemStatus } from "common";
import React from "react";
import { useMutation } from "react-query";
import { Virtuoso } from "react-virtuoso";
import { Event, PurchaseItem } from "types";
import { api } from "../../api/api";
import { useInfiniteCollection } from "../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { EventNoteList, PurchaseNoteList } from "../Common/NoteList";
import { SecondaryButton } from "../Common/SecondaryButton";
import { Loader } from "../Loader";
import { EventSummaryTile } from "./EventSummaryTile";

interface EventNotesModalProps {
    eventId?: ID;
    onClose: (e: any) => void;
    isOpen: boolean;
}

export const EventNotesModal = ({ eventId, onClose, isOpen }: EventNotesModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent eventId={eventId} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    eventId?: ID;
    onClose: (e: any) => void;
}

const ModalContent = ({ eventId, onClose }: ModalContentProps) => {
    const invalidate = useInvalidate();
    const { data: event, isLoading: isLoadingEvent } = useResource<Event>(["events", eventId]);

    const {
        data: purchaseItems,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteCollection<PurchaseItem>("purchaseItems", { event: eventId });

    const { mutateAsync: deleteNoteItem } = useMutation(async (noteId: ID) => {
        try {
            return await api.delete(`/events/${eventId}/notes/${noteId}`);
        } catch {
            flash.danger("Error deleting note");
        }
    });

    const handleDeleteNoteItem = async (noteId: ID) => {
        await deleteNoteItem(noteId);
        invalidate("events", eventId);
    };

    if (isLoadingEvent) {
        return <Loader />;
    }

    const itemsWithNotes = purchaseItems.filter(
        (item) => item.purchase.notes.length > 0 && item.status !== PurchaseItemStatus.CANCELED,
    );

    return (
        <>
            <Modal.Header>
                <div className="flex items-center">
                    <span className="w-1/3" />
                    <span className="w-1/3">Notes</span>
                    {/* hiding as part of the X2-6792 requirements, since Print and Export are disabled permanently */}
                    <div className="flex hidden w-1/3 justify-end space-x-2">
                        <SecondaryButton disabled size="small" icon={<PrintIcon />}>
                            Print
                        </SecondaryButton>
                        <SecondaryButton disabled size="small" icon={<ExportIcon />}>
                            Export
                        </SecondaryButton>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body>
                <EventSummaryTile event={event!} />

                {event?.notes?.length ? (
                    <div className="mt-1">
                        <p className="mb-4 text-md font-bold leading-p1 text-black">Event Notes</p>
                        <EventNoteList event={event} onDelete={handleDeleteNoteItem} />
                    </div>
                ) : null}

                {itemsWithNotes.length > 0 ? (
                    <p className="mt-10 mb-4 text-md font-bold leading-p1 text-black">Purchase Notes</p>
                ) : null}

                <Virtuoso
                    style={{ height: 200 }}
                    data={itemsWithNotes}
                    endReached={async () => {
                        if (hasNextPage) {
                            fetchNextPage();
                        }
                    }}
                    itemContent={(index, item) => {
                        return <PurchaseNoteList purchase={item.purchase} />;
                    }}
                />

                {isFetchingNextPage ? <Loader /> : null}
            </Modal.Body>

            <Modal.Footer>
                <Button color="primary" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </>
    );
};
